/**
 * The approvals list shared by the workspace card and the Company page:
 * each approved / denied arche, its origin, a revoke, and the fourth
 * non-negotiable — what the arche has DONE here. On a workspace that is
 * every invocation with its outcome and reader, failed calls included (a
 * burst of refusals is exactly what an admin wants to see); on a Company it
 * is per-workspace counts only, because a Company role grants no data
 * access and so learns nothing about who read what.
 *
 * An approval is PINNED to the reach its disclosure named. Growth since —
 * new permissions, a data class that opted in later — is shown, and new
 * reach is refused until someone approves again ("Needs re-approval"); an
 * arche that is no longer live, and publishers added since the decision,
 * are shown too. Each row names who decided and, because publishing an arche
 * never bars approving it (ADR-022 amendment 2026-10-14), whether they
 * publish it.
 *
 * Revoke and Deny are the destructive half of the model and ask first;
 * Approve on a denied row and Re-approve on a grown one hand off to the
 * disclosure dialog (`onApprove`), so no approval is ever recorded without
 * the reach having been shown. A workspace owner's deny is sticky: an admin
 * is not offered Approve or Revoke on it (the server refuses them too).
 */

import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ArcheApprovalDto, ArcheReadActivityDto, CompanyArcheActivityDto } from '@/lib/api';
import { describeCompanyError } from '../lib/company-errors';

/**
 * Where an approved arche's activity comes from: a workspace's own record
 * (per invocation, with the reader), or a Company's per-workspace counts.
 */
export type ApprovalActivitySource =
  | { kind: 'members'; load: (archeId: string) => Promise<ArcheReadActivityDto[]> }
  | {
      kind: 'workspaces';
      load: (archeId: string) => Promise<CompanyArcheActivityDto[]>;
      /** The linked workspace's name, for a holder id. */
      workspaceName: (holderId: string) => string;
    };

type LoadedActivity =
  | { kind: 'members'; rows: ArcheReadActivityDto[] }
  | { kind: 'workspaces'; rows: CompanyArcheActivityDto[] };

interface ApprovalsListProps {
  approvals: Array<ArcheApprovalDto & { origin?: 'workspace' | 'company' }>;
  isLoading: boolean;
  /** Whether the viewer may revoke rows (owner/admin of THIS scope). */
  canManage: boolean;
  /** Rows from another scope (a Company row on a workspace) are read-only here. */
  revokableOrigin?: 'workspace' | 'company';
  onRevoke: (approvalId: string) => Promise<unknown>;
  /**
   * Workspace scope only: record a DENY for this arche here. A workspace deny
   * beats a Company approval and the Trust Fias list, so every approved row
   * (whatever its origin) and every trusted arche gets the action.
   */
  onDeny?: (archeId: string) => Promise<unknown>;
  /**
   * Offered on denied rows in place of Revoke, and as Re-approve on an
   * approval whose arche has grown: open the disclosure for this arche so
   * the admin approves with the reach in front of them. The caller records
   * the decision; nothing is sent from here.
   */
  onApprove?: (archeId: string) => void;
  activitySource: ApprovalActivitySource;
  /**
   * The viewer's role on this scope, when it matters: an owner's deny is
   * sticky, so an admin is not offered Approve or Revoke on one.
   */
  viewerRole?: string;
  /** Paging (Company scope): more decisions exist than are loaded. */
  hasMore?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  /** "Trust Fias arches" is on for this scope (or its Company). */
  fiasTrusted?: boolean;
  /** The first-party arches that trust covers, by name (empty until curated). */
  fiasTrustedArches?: Array<{ archeId: string; name: string }>;
}

type PendingAction =
  | { kind: 'revoke'; approvalId: string; archeName: string; origin?: 'workspace' | 'company' }
  | { kind: 'deny'; archeId: string; archeName: string; trusted: boolean };

export function ApprovalsList({
  approvals,
  isLoading,
  canManage,
  revokableOrigin,
  onRevoke,
  onDeny,
  onApprove,
  activitySource,
  viewerRole,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  fiasTrusted = false,
  fiasTrustedArches = [],
}: ApprovalsListProps) {
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [activityFor, setActivityFor] = useState<string | null>(null);
  const [activity, setActivity] = useState<LoadedActivity | null>(null);
  const [activityError, setActivityError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // Which activity request is current: a slower earlier response must not
  // land under a later row's heading.
  const activityRequest = useRef(0);

  function toggleRow(id: string): void {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function runPending(): Promise<void> {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      if (pending.kind === 'revoke') await onRevoke(pending.approvalId);
      else if (onDeny) await onDeny(pending.archeId);
      setPending(null);
    } catch (err) {
      setError(
        describeCompanyError(
          err,
          pending.kind === 'revoke' ? 'Could not revoke.' : 'Could not deny.',
        ),
      );
      setPending(null);
    } finally {
      setBusy(false);
    }
  }

  async function showActivity(archeId: string): Promise<void> {
    if (activityFor === archeId) {
      activityRequest.current += 1;
      setActivityFor(null);
      setActivity(null);
      return;
    }
    const token = ++activityRequest.current;
    setActivityFor(archeId);
    setActivity(null);
    setActivityError(null);
    try {
      const loaded: LoadedActivity =
        activitySource.kind === 'members'
          ? { kind: 'members', rows: await activitySource.load(archeId) }
          : { kind: 'workspaces', rows: await activitySource.load(archeId) };
      if (token === activityRequest.current) setActivity(loaded);
    } catch (err) {
      // This is the audit view: a failed load must never read as "the arche
      // did nothing here".
      if (token === activityRequest.current) {
        setActivityError(describeCompanyError(err, 'Could not load activity.'));
      }
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading approvals…</p>;
  if (approvals.length === 0 && !fiasTrusted) {
    return <p className="text-sm text-muted-foreground">No arches have been approved here.</p>;
  }

  return (
    <div className="space-y-2">
      {error && <p className="text-sm text-destructive">{error}</p>}
      {fiasTrusted && (
        <div className="rounded-md bg-muted/40 px-3 py-2 text-sm">
          <p className="text-foreground">
            <Badge variant="secondary" className="mr-2">
              Trust Fias
            </Badge>
            Fias first-party arches are approved here, from a fixed reviewed list.
          </p>
          {fiasTrustedArches.length === 0 ? (
            <p className="mt-1 text-xs text-muted-foreground">
              The list is empty for now; arches Fias adds to it will appear here automatically.
            </p>
          ) : (
            <ul className="mt-1 flex flex-wrap items-center gap-2">
              {fiasTrustedArches.map((a) => (
                <li key={a.archeId} className="flex items-center gap-1">
                  <Badge variant="outline">{a.name}</Badge>
                  {onDeny && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 px-2 text-xs"
                      onClick={() =>
                        setPending({
                          kind: 'deny',
                          archeId: a.archeId,
                          archeName: a.name,
                          trusted: true,
                        })
                      }
                      disabled={busy}
                      aria-label={`Deny ${a.name} here`}
                    >
                      Deny here
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {approvals.length === 0 && (
        <p className="text-sm text-muted-foreground">No other arches have been approved here.</p>
      )}
      <ul className="flex flex-col divide-y divide-border border-y border-border">
        {approvals.map((a) => {
          const name = a.archeName ?? `Arche …${a.archeId.slice(-6)}`;
          const decider = a.decidedByLabel ?? 'someone';
          const ownRow =
            canManage &&
            (revokableOrigin === undefined ||
              a.origin === undefined ||
              a.origin === revokableOrigin);
          // A workspace owner's deny is sticky: only an owner changes it, so
          // an admin gets neither Approve nor Revoke on it.
          const ownerDenyLocked =
            ownRow &&
            a.decision === 'deny' &&
            a.decidedByRole === 'owner' &&
            viewerRole !== undefined &&
            viewerRole !== 'owner';
          // A denied row offers Approve (replacing the deny); Revoke would only
          // clear the deny and fall back to the Company or Trust Fias, which is
          // not what someone clicking there means.
          const approvable =
            ownRow && !ownerDenyLocked && a.decision === 'deny' && onApprove !== undefined;
          const revokable = ownRow && !ownerDenyLocked && !approvable;
          // Growth since the decision is refused until someone approves again —
          // through the disclosure, like any approval.
          const grown =
            a.decision === 'approve' &&
            (a.needsReapproval || a.permissionsAddedSinceApproval.length > 0);
          const reapprovable = ownRow && grown && onApprove !== undefined;
          return (
            <li key={a.approvalId} className="py-2 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate font-medium text-foreground">
                    {name}{' '}
                    <Badge variant={a.decision === 'approve' ? 'secondary' : 'destructive'}>
                      {a.decision === 'approve' ? 'Approved' : 'Denied'}
                    </Badge>
                    {a.origin === 'company' && (
                      <Badge variant="outline" className="ml-1">
                        via Company
                      </Badge>
                    )}
                    {a.decision === 'approve' && a.needsReapproval && (
                      <Badge
                        variant="outline"
                        className="ml-1 border-amber-500 text-amber-700 dark:text-amber-400"
                      >
                        Needs re-approval
                      </Badge>
                    )}
                    {a.archeInactive && (
                      <Badge variant="outline" className="ml-1">
                        Arche unavailable
                      </Badge>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {a.decision === 'approve' ? 'Approved' : 'Denied'} by {decider}
                    {a.decidedByRole ? ` (${a.decidedByRole})` : ''} ·{' '}
                    {new Date(a.decidedAt).toLocaleDateString()}
                    {a.note ? ` · ${a.note}` : ''}
                  </p>
                  {a.decision === 'approve' && a.deciderPublishesArche && (
                    <p className="text-xs text-muted-foreground">
                      {decider} publishes this arche, so this approval is for their own code.
                    </p>
                  )}
                  {a.decision === 'approve' &&
                    !a.deciderPublishesArche &&
                    a.deciderGovernsArche && (
                      <p className="text-xs text-muted-foreground">
                        {decider} is an owner or admin of the Company that owns this arche, so they
                        choose who publishes it.
                      </p>
                    )}
                  {a.decision === 'approve' && a.needsReapproval && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      It can now reach {a.newReachEntityIds.length} more kind
                      {a.newReachEntityIds.length === 1 ? '' : 's'} of data than this approval
                      showed. That new reach is refused until someone approves it again; what was
                      approved keeps working.
                    </p>
                  )}
                  {a.permissionsAddedSinceApproval.length > 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Declares new permissions since this decision:{' '}
                      {a.permissionsAddedSinceApproval.join(', ')}. Anything that needs them is
                      refused until someone approves again.
                    </p>
                  )}
                  {a.archeInactive && (
                    <p className="text-xs text-muted-foreground">
                      This arche is suspended or no longer published. It reads nothing here while it
                      is unavailable.
                    </p>
                  )}
                  {a.publishersAddedSinceApproval > 0 && (
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      {a.publishersAddedSinceApproval} new publisher
                      {a.publishersAddedSinceApproval === 1 ? '' : 's'} since approval — people who
                      can now ship this arche&apos;s code.
                    </p>
                  )}
                  {ownerDenyLocked && (
                    <p className="text-xs text-muted-foreground">
                      Denied by a workspace owner. Only an owner can change it.
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* The activity read is owner/admin-only server-side, like revoke. */}
                  {canManage && a.decision === 'approve' && (
                    <Button size="sm" variant="ghost" onClick={() => showActivity(a.archeId)}>
                      {activityFor === a.archeId ? 'Hide activity' : 'Activity'}
                    </Button>
                  )}
                  {onDeny && canManage && a.decision === 'approve' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setPending({
                          kind: 'deny',
                          archeId: a.archeId,
                          archeName: name,
                          trusted: false,
                        })
                      }
                      disabled={busy}
                      aria-label={`Deny ${name} here`}
                    >
                      Deny here
                    </Button>
                  )}
                  {reapprovable && (
                    <Button
                      size="sm"
                      onClick={() => onApprove?.(a.archeId)}
                      disabled={busy}
                      aria-label={`Re-approve ${name} here`}
                    >
                      Re-approve
                    </Button>
                  )}
                  {approvable && (
                    <Button
                      size="sm"
                      onClick={() => onApprove?.(a.archeId)}
                      disabled={busy}
                      aria-label={`Approve ${name} here`}
                    >
                      Approve
                    </Button>
                  )}
                  {revokable && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setPending({
                          kind: 'revoke',
                          approvalId: a.approvalId,
                          archeName: name,
                          origin: a.origin,
                        })
                      }
                      disabled={busy}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
              {activityFor === a.archeId && (
                <div className="mt-2 rounded-md border border-border bg-muted/30 p-2 text-xs">
                  {activityError ? (
                    <p className="text-destructive">{activityError}</p>
                  ) : activity === null ? (
                    <p className="text-muted-foreground">Loading…</p>
                  ) : activity.rows.length === 0 ? (
                    <p className="text-muted-foreground">No activity in the last 30 days.</p>
                  ) : activity.kind === 'workspaces' ? (
                    <>
                      <ul className="space-y-1">
                        {activity.rows.map((row) => (
                          <li
                            key={row.holderId}
                            className="flex flex-wrap items-start justify-between gap-2 px-1"
                          >
                            <span className="min-w-0 flex-1 truncate text-foreground">
                              {activitySource.kind === 'workspaces'
                                ? activitySource.workspaceName(row.holderId)
                                : row.holderId}
                            </span>
                            <span className="shrink-0 text-muted-foreground">
                              {row.reads} read{row.reads === 1 ? '' : 's'} · {row.distinctMembers}{' '}
                              member{row.distinctMembers === 1 ? '' : 's'} · last{' '}
                              {new Date(row.lastReadAt).toLocaleString()}
                            </span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2 text-muted-foreground">
                        Counts per workspace since it linked. Who read what is shown to each
                        workspace&apos;s own owners and admins, not to the Company.
                      </p>
                    </>
                  ) : (
                    <ul className="space-y-1">
                      {activity.rows.slice(0, 50).map((row) => {
                        const open = expandedRows.has(row.invocationId);
                        const who = row.userLabel ?? `user …${row.userId.slice(-6)}`;
                        return (
                          <li key={row.invocationId}>
                            {/* Narrow screens clip the row; a tap opens the full record. */}
                            <button
                              type="button"
                              onClick={() => toggleRow(row.invocationId)}
                              aria-expanded={open}
                              className="flex w-full items-start justify-between gap-2 rounded px-1 text-left hover:bg-muted/50"
                            >
                              <span
                                className={
                                  open ? 'min-w-0 flex-1 break-words' : 'min-w-0 flex-1 truncate'
                                }
                              >
                                {row.verb ?? row.entityId} · {who}
                              </span>
                              <span className="shrink-0 text-muted-foreground">
                                {new Date(row.startedAt).toLocaleString()} · {row.outcome}
                              </span>
                            </button>
                            {open && (
                              <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 px-1 text-muted-foreground">
                                <dt>Who</dt>
                                <dd className="break-all text-foreground">
                                  {who} · {row.userId}
                                </dd>
                                <dt>Verb</dt>
                                <dd className="break-all">{row.verb ?? '—'}</dd>
                                <dt>Entity</dt>
                                <dd className="break-all">{row.entityId}</dd>
                                <dt>Workspace</dt>
                                <dd className="break-all">{row.holderId}</dd>
                                <dt>Invocation</dt>
                                <dd className="break-all">{row.invocationId}</dd>
                              </dl>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {hasMore && onLoadMore && (
        <Button size="sm" variant="outline" onClick={onLoadMore} disabled={isLoadingMore}>
          {isLoadingMore ? 'Loading…' : 'Show more'}
        </Button>
      )}
      <AlertDialog open={pending !== null} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pending?.kind === 'revoke'
                ? `Revoke ${pending.archeName}?`
                : pending
                  ? `Deny ${pending.archeName} here?`
                  : ''}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pending?.kind === 'revoke'
                ? pending.origin === 'company'
                  ? 'Immediately, in every linked workspace: the arche loses the reach this approval gave it. A workspace’s own approval of it, if any, stays.'
                  : 'Immediately: the arche loses the reach this approval gave it here. If the Company approves it or Trust Fias covers it, that still applies unless you deny it here.'
                : pending?.kind === 'deny'
                  ? pending.trusted
                    ? 'A denial here overrides Trust Fias for this arche in this workspace only, immediately. You can approve it again later.'
                    : 'A denial here overrides any Company approval for this arche in this workspace only, immediately. You can approve it again later.'
                  : ''}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                void runPending();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {busy ? 'Working…' : pending?.kind === 'revoke' ? 'Revoke' : 'Deny here'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
